import React, { useState, useEffect, ChangeEvent } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { theme } from '../../theme';

interface Contrato {
  id: number;
  ncontrato: string;
  tipo: string;
  producto_nombre: string;
  producto_id: number;
  fecha: string;
  comprador_nombre: string;
  comprador_id: number;
  vendedor_nombre: string;
  vendedor_id: number;
  despachante_nombre: string;
  corredor_nombre: string;
  entregador_nombre: string;
  destinatario_nombre: string;
  estado: string;
  tarafardo: number;
  kilos: number;
  precio: number;
  fecha_contrato: string;
  fecha_cierre: string;
}

interface Producto {
  id: number;
  descripcion: string;
  tipo: number;
}

interface Provincia {
  id: number;
  provincia: string;
}

interface Localidad {
  id: number;
  localidad: string;
  provincia: number;
}

interface Padron {
  id: number;
  denominacion: string;
  nc: string;
}

interface StockDisponible {
  id: number;
  descripcion: string;
  cantidad: number;
  ubicacion: string;
}

interface Aplicada {
  id: number;
  descripcion: string;
  cantidad: number;
  fecha: string;
  aplicado?: boolean;
  stock?: number;
  alta?: boolean;
  orden?: string;
  id_fardo?: number | null;
  id_orden?: number | null;
  nro_lote?: number | null;
}

interface SiloBolsa {
  id: number;
  numero_silobolsa: string;
  kilos: number;
  ubicacion: string;
  observacion: string;
  fecha_alta: string;
  fecha_cierre: string;
  estado: string;
  producto: string;
}

const AltaOrdenes = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [tipoOrden, setTipoOrden] = useState('');
  const [contrato, setContrato] = useState('');
  const [producto, setProducto] = useState('');
  const [destinatario, setDestinatario] = useState('');
  const [entregador, setEntregador] = useState('');
  const [empresa, setEmpresa] = useState('');
  const [direccion, setDireccion] = useState('');
  const [provincia, setProvincia] = useState('');
  const [localidad, setLocalidad] = useState('');
  const [origen, setOrigen] = useState('');
  const [cliente, setCliente] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);

  // Estados para los datos de los selects
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [provincias, setProvincias] = useState<Provincia[]>([]);
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [destinatarios, setDestinatarios] = useState<Padron[]>([]);
  const [entregadores, setEntregadores] = useState<Padron[]>([]);
  const [origenes, setOrigenes] = useState<Padron[]>([]);
  const [clientes, setClientes] = useState<Padron[]>([]);
  const [silosBolsas, setSilosBolsas] = useState<SiloBolsa[]>([]);
  const [siloBolsaSeleccionado, setSiloBolsaSeleccionado] = useState('');
  const [carga, setCarga] = useState('');
  const [primerEnvioRealizado, setPrimerEnvioRealizado] = useState(false);

  // Estados para stock y aplicadas
  const [stockDisponible, setStockDisponible] = useState<StockDisponible[]>([]);
  const [aplicadas, setAplicadas] = useState<Aplicada[]>([]);
  const [resumen, setResumen] = useState<any>(null);
  const [fardosProcesados, setFardosProcesados] = useState<Set<number>>(new Set()); // Matriz de fardos ya procesados
  const [cargando, setCargando] = useState(false);
  const [grabarCargando, setGrabarCargando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [tipoMensaje, setTipoMensaje] = useState<'success' | 'error' | ''>('');

  const tiposOrden = [
    { value: 'C', label: 'Compra' },
    { value: 'V', label: 'Venta' },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        setCargando(true);
        setMensaje('');
        console.log('Iniciando carga de datos...');
        const token = await getAccessTokenSilently();
        
        // Obtener id_empresa del usuario de Auth0
        console.log('Usuario:', user);
        const idEmpresa = user?.id_empresa;
        console.log('ID Empresa del usuario:', idEmpresa);

        if (!idEmpresa) {
          setMensaje('No se encontró el ID de empresa en el usuario');
          setTipoMensaje('error');
          return;
        }

        // Cargar datos del padrón
        const padronUrl = `${process.env.REACT_APP_PADRON_URL}/leer_padron/${idEmpresa}/`;
        console.log('URL del padrón:', padronUrl);
        const padronRes = await fetch(padronUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!padronRes.ok) {
          throw new Error(`Error al cargar padrón: ${padronRes.status}`);
        }

        const padronData = await padronRes.json();
        console.log('Datos del padrón cargados:', padronData);

        // Asignar todos los datos del padrón a cada select
        setDestinatarios(padronData);
        setEntregadores(padronData);
        setOrigenes(padronData);
        setClientes(padronData);

        // Cargar provincias
        const url = `${process.env.REACT_APP_COMMON_URL}/listar_provincias/`;
        console.log('URL de la petición:', url);
        const provinciasRes = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const provinciasData = await provinciasRes.json();
        console.log('Provincias cargadas:', provinciasData);
        setProvincias(provinciasData);

        // Cargar productos
        const productosUrl = `${process.env.REACT_APP_BALANZA_URL}/productos/`;
        const productosRes = await fetch(productosUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const productosData = await productosRes.json();
        console.log('Productos cargados:', productosData);
        setProductos(productosData);

      } catch (error) {
        console.error('Error al cargar datos:', error);
        setMensaje('Error al cargar los datos. Por favor, intente nuevamente.');
        setTipoMensaje('error');
      } finally {
        setCargando(false);
      }
    };

    fetchData();
  }, [getAccessTokenSilently, user]);

  // Cargar contratos cuando cambia el tipo de orden
  useEffect(() => {
    const cargarContratos = async () => {
      // Solo cargar contratos si el tipo de orden es Compra (C) o Venta (V)
      if (tipoOrden === 'C' || tipoOrden === 'V') {
        try {
          setCargando(true);
          const token = await getAccessTokenSilently();
          const idEmpresa = user?.id_empresa;

          if (!idEmpresa) {
            console.error('No se encontró el ID de empresa en el usuario');
            return;
          }

          console.log('Cargando contratos para tipo de orden:', tipoOrden);
          const estado = 3; 
          const contratosUrl = `${process.env.REACT_APP_ORDENES_URL}/listado_contratos/${idEmpresa}/${estado}/`;
          const contratosRes = await fetch(contratosUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (!contratosRes.ok) {
            throw new Error(`Error al cargar contratos: ${contratosRes.status}`);
          }

          const contratosData = await contratosRes.json();
          console.log('Contratos cargados:', contratosData);

          // Extraer los resultados de la respuesta paginada
          const contratosArray = contratosData.results || contratosData || [];
          console.log('Array de contratos:', contratosArray);

          // Verificar que contratosArray sea un array antes de filtrar
          if (!Array.isArray(contratosArray)) {
            console.error('Los datos de contratos no son un array:', contratosArray);
            setContratos([]);
            return;
          }

          // Filtrar contratos por tipo coincidente con el tipo de orden y estado activo
          const contratosFiltrados = contratosArray.filter((contrato: any) => {
            // Excluir contratos cerrados o dados de baja
            if (contrato.estado === 'Cerrada' || contrato.estado === 'Baja') {
              return false;
            }
            
            // Si es Compra (C), mostrar contratos de tipo C
            if (tipoOrden === 'C' && contrato.tipo === 'C') {
              return true;
            }
            // Si es Venta (V), mostrar contratos de tipo V
            if (tipoOrden === 'V' && contrato.tipo === 'V') {
              return true;
            }
            return false;
          });

          console.log('Contratos filtrados:', contratosFiltrados);
          setContratos(contratosFiltrados);

        } catch (error) {
          console.error('Error al cargar contratos:', error);
          setMensaje('Error al cargar los contratos');
          setTipoMensaje('error');
        } finally {
          setCargando(false);
        }
      } else {
        // Si no es Compra ni Venta, limpiar contratos y campo contrato
        setContratos([]);
        setContrato('');
      }
    };

    cargarContratos();
  }, [tipoOrden, getAccessTokenSilently, user]);

  // Cargar localidades cuando cambia la provincia
  useEffect(() => {
    const fetchLocalidades = async () => {
      if (provincia) {
        try {
          const token = await getAccessTokenSilently();
          console.log('Cargando localidades para provincia:', provincia);
          const url = `${process.env.REACT_APP_COMMON_URL}/listar_localidades/${provincia}/`;
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          const localidadesData = await response.json();
          console.log('Localidades cargadas:', localidadesData);
          setLocalidades(localidadesData);
        } catch (error) {
          console.error('Error al cargar localidades:', error);
          setMensaje('Error al cargar las localidades');
          setTipoMensaje('error');
        }
      } else {
        setLocalidades([]); // Limpiar localidades si no hay provincia seleccionada
      }
    };

    fetchLocalidades();
  }, [provincia, getAccessTokenSilently]);

  // Cargar silos bolsas cuando cambia el producto a tipo 3
  useEffect(() => {
    const fetchSilosBolsas = async () => {
      const productoSeleccionado = productos.find(p => p.id.toString() === producto);
      if (productoSeleccionado && productoSeleccionado.tipo === 3) {
        try {
          const token = await getAccessTokenSilently();
          const idEmpresa = user?.id_empresa;
          const idSucursal = user?.id_sucursal;

          if (!idEmpresa || !idSucursal) {
            console.error('No se encontró el ID de empresa o sucursal en el usuario');
            return;
          }

          console.log('Cargando silos bolsas para producto tipo 3');
          const url = `${process.env.REACT_APP_SILOBOLSA_URL}/listado_silobolsa/${idEmpresa}/${idSucursal}/A/`;
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error(`Error al cargar silos bolsas: ${response.status}`);
          }

          const data = await response.json();
          console.log('Silos bolsas cargados:', data);
          setSilosBolsas(data.silos_bolsas || []);
        } catch (error) {
          console.error('Error al cargar silos bolsas:', error);
          setMensaje('Error al cargar los silos bolsas');
          setTipoMensaje('error');
          setSilosBolsas([]);
        }
      } else {
        setSilosBolsas([]);
        setSiloBolsaSeleccionado('');
        setCarga('');
      }
    };

    fetchSilosBolsas();
  }, [producto, productos, getAccessTokenSilently, user]);

  // Cargar stock disponible cuando cambian los filtros
  // useEffect(() => {
  //   // Código comentado para cargar stock disponible
  // }, [producto, origen, getAccessTokenSilently]);

  const handleTipoOrdenChange = (event: ChangeEvent<HTMLSelectElement>) => {
    // Limpiar todos los datos capturados
    setProducto('');
    setDestinatario('');
    setEntregador('');
    setEmpresa('');
    setDireccion('');
    setProvincia('');
    setLocalidad('');
    setOrigen('');
    setCliente('');
    setDesde('');
    setHasta('');
    setSiloBolsaSeleccionado('');
    setCarga('');
    setAplicadas([]);
    setResumen(null);
    setFardosProcesados(new Set());
    setMensaje('');
    setTipoMensaje('');
    setPrimerEnvioRealizado(false);
    const nuevoTipoOrden = event.target.value;
    setTipoOrden(nuevoTipoOrden);
    
    // Si se selecciona tipo de orden "C" (Compra), asignar cliente predeterminado
    if (nuevoTipoOrden === 'C') {
      const clienteCompra = clientes.find(c => c.nc === '000000');
      if (clienteCompra) {
        setCliente(clienteCompra.id.toString());
        console.log('Cliente predeterminado asignado para compra:', clienteCompra.denominacion);
      }
    }
  };

  const handleContratoChange = (event: ChangeEvent<HTMLSelectElement>) => {
    // Limpiar todos los datos capturados
    setProducto('');
    setDestinatario('');
    setEntregador('');
    setEmpresa('');
    setDireccion('');
    setProvincia('');
    setLocalidad('');
    setOrigen('');
    setCliente('');
    setDesde('');
    setHasta('');
    setSiloBolsaSeleccionado('');
    setCarga('');
    setAplicadas([]);
    setResumen(null);
    setFardosProcesados(new Set());
    setMensaje('');
    setTipoMensaje('');
    setPrimerEnvioRealizado(false);
    const contratoId = event.target.value;
    setContrato(contratoId);
    
    // Si se seleccionó un contrato, capturar el producto, destinatario y entregador automáticamente
    if (contratoId) {
      const contratoSeleccionado = contratos.find(c => c.id.toString() === contratoId);
      if (contratoSeleccionado) {
        console.log('Contrato seleccionado:', contratoSeleccionado);
        console.log('Producto ID del contrato:', contratoSeleccionado.producto_id);
        console.log('Destinatario del contrato:', contratoSeleccionado.destinatario_nombre);
        console.log('Entregador del contrato:', contratoSeleccionado.entregador_nombre);
        
        // Capturar producto automáticamente
        if (contratoSeleccionado.producto_id) {
          setProducto(contratoSeleccionado.producto_id.toString());
        }
        
        // Establecer tipo de orden basado en el tipo de contrato
        if (contratoSeleccionado.tipo === 'V') {
          setTipoOrden('V');
        } else if (contratoSeleccionado.tipo === 'C') {
          setTipoOrden('C');
        }
        
        // Buscar y asignar destinatario
        const destinatarioEncontrado = destinatarios.find(d => 
          d.denominacion === contratoSeleccionado.destinatario_nombre
        );
        if (destinatarioEncontrado && destinatarioEncontrado.id) {
          setDestinatario(destinatarioEncontrado.id.toString());
        }
        
        // Buscar y asignar entregador
        const entregadorEncontrado = entregadores.find(e => 
          e.denominacion === contratoSeleccionado.entregador_nombre
        );
        if (entregadorEncontrado && entregadorEncontrado.id) {
          setEntregador(entregadorEncontrado.id.toString());
        }

        // Si el contrato es de tipo "C" (Compra)
        if (contratoSeleccionado.tipo === 'C') {
          // Origen se pre-llena con el vendedor del contrato
          if (contratoSeleccionado.vendedor_id) {
            // Buscar el vendedor en la lista de orígenes
            const vendedorEncontrado = origenes.find(o => 
              o.id === contratoSeleccionado.vendedor_id
            );
            if (vendedorEncontrado && vendedorEncontrado.id) {
              setOrigen(vendedorEncontrado.id.toString());
              console.log('Origen predeterminado con vendedor para compra:', vendedorEncontrado.denominacion);
            }
          }
          
          // Para órdenes de Compra, buscar el cliente con nc='000000'
          const clienteCompra = clientes.find(c => c.nc === '000000');
          if (clienteCompra) {
            setCliente(clienteCompra.id.toString());
            console.log('Cliente predeterminado para compra:', clienteCompra.denominacion);
          } else {
            console.log('No se encontró cliente con nc=000000 para órdenes de compra');
            setCliente('');
          }
        } else if (contratoSeleccionado.tipo === 'V') {
          // Si el contrato es de tipo "V" (Venta)
          // Cliente se pre-llena con el vendedor del contrato
          if (contratoSeleccionado.vendedor_id) {
            // Buscar el vendedor en la lista de clientes
            const vendedorEncontrado = clientes.find(c => 
              c.id === contratoSeleccionado.vendedor_id
            );
            if (vendedorEncontrado && vendedorEncontrado.id) {
              setCliente(vendedorEncontrado.id.toString());
              console.log('Cliente predeterminado con vendedor para venta:', vendedorEncontrado.denominacion);
            }
          }
          
          // Origen se deja vacío para que el usuario lo seleccione manualmente
          setOrigen('');
          console.log('Origen dejado vacío para selección manual en venta');
        } else {
          // Para otros tipos de contrato, mantener la lógica original
          if (contratoSeleccionado.vendedor_id) {
            // Buscar el vendedor en la lista de orígenes
            const vendedorEncontrado = origenes.find(o => 
              o.id === contratoSeleccionado.vendedor_id
            );
            if (vendedorEncontrado && vendedorEncontrado.id) {
              setOrigen(vendedorEncontrado.id.toString());
              console.log('Origen predeterminado con vendedor:', vendedorEncontrado.denominacion);
            }
          }
        }

        // Llenar automáticamente los campos de dirección, empresa, provincia y localidad
        // Estos campos se pueden obtener del contrato o usar valores por defecto
        if (contratoSeleccionado.destinatario_nombre) {
          setEmpresa(contratoSeleccionado.destinatario_nombre);
        }
        
        // Para la dirección, usar un valor por defecto o del contrato si está disponible
        setDireccion('Mariano Moreno 241'); // Valor por defecto
        
        // Para provincia, buscar "CHACO" en la lista de provincias
        const provinciaChaco = provincias.find(p => p.provincia === 'CHACO');
        if (provinciaChaco) {
          setProvincia(provinciaChaco.id.toString());
          
          // Para localidad, buscar "BAJO HONDO" en la lista de localidades de CHACO
          const localidadBajoHondo = localidades.find(l => 
            l.localidad === 'BAJO HONDO' && l.provincia === provinciaChaco.id
          );
          if (localidadBajoHondo) {
            setLocalidad(localidadBajoHondo.id.toString());
          }
        }
        
        console.log('Campos automáticos llenados:', {
          tipoOrden: contratoSeleccionado.tipo === 'V' ? 'V' : contratoSeleccionado.tipo === 'C' ? 'C' : '',
          producto: contratoSeleccionado.producto_id,
          empresa: contratoSeleccionado.destinatario_nombre,
          direccion: 'Mariano Moreno 241',
          provincia: provinciaChaco?.id,
          localidad: localidades.find(l => l.localidad === 'BAJO HONDO' && l.provincia === provinciaChaco?.id)?.id
        });

      }
    } else {
      // Si se deseleccionó el contrato, limpiar los campos
      setProducto('');
      setDestinatario('');
      setEntregador('');
      setOrigen(''); // Limpiar también el origen
      setCliente(''); // Limpiar también el cliente
      setAplicadas([]);
    }
  };

  // Función para cargar fardos pendientes de un contrato
//  const cargarFardosPendientes = async (contratoId: string) => {
//    try {
//      setCargando(true);
//      const token = await getAccessTokenSilently();
//      const idEmpresa = user?.id_empresa;

//      if (!idEmpresa) {
//        console.error('No se encontró el ID de empresa en el usuario');
//        return;
//      }

//      console.log('Cargando fardos pendientes para contrato:', contratoId);
//      const url = `${process.env.REACT_APP_ORDENES_URL}/fardos_pendiente_contrato/${idEmpresa}/${contratoId}/`;
//      const response = await fetch(url, {
//        method: 'GET',
//        headers: {
//          'Authorization': `Bearer ${token}`,
//          'Content-Type': 'application/json'
//        }
//      });

//      if (!response.ok) {
//        throw new Error(`Error al cargar fardos pendientes: ${response.status}`);
//      }

//      const data = await response.json();
//      console.log('Fardos pendientes cargados:', data);

//      // Convertir los fardos pendientes al formato esperado por el componente
//      const fardosPendientes = data.fardos_pendientes || [];
//      const aplicadasFormateadas = fardosPendientes.map((fardo: any) => ({
//        id: fardo.id || fardo.numero_fardo,
//        descripcion: `Fardo ${fardo.numero_fardo} - ${fardo.producto_descripcion || 'Producto'}`,
//        cantidad: fardo.kilos_fardo || 0,
//        fecha: new Date(fardo.fecha_hora).toISOString().split('T')[0] // Fecha del fardo
//      }));

//      setAplicadas(aplicadasFormateadas);
//      setMensaje(`Se encontraron ${aplicadasFormateadas.length} fardos pendientes para el contrato`);
//      setTipoMensaje('success');

//    } catch (error) {
//      console.error('Error al cargar fardos pendientes:', error);
//      setMensaje('Error al cargar los fardos pendientes del contrato');
//      setTipoMensaje('error');
//      setAplicadas([]);
//    } finally {
//      setCargando(false);
//    }
//  };

  const handleProductoChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setProducto(event.target.value);
  };

  const handleDestinatarioChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setDestinatario(event.target.value);
  };

  const handleEntregadorChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setEntregador(event.target.value);
  };

  const handleProvinciaChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setProvincia(event.target.value);
    setLocalidad(''); // Resetear localidad cuando cambia la provincia
  };

  const handleLocalidadChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setLocalidad(event.target.value);
  };

  const handleEmpresaChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmpresa(event.target.value);
  };

  const handleDireccionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDireccion(event.target.value);
  };

  const handleDesdeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDesde(event.target.value);
  };

  const handleHastaChange = (event: ChangeEvent<HTMLInputElement>) => {
    setHasta(event.target.value);
  };

  const handleSiloBolsaChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSiloBolsaSeleccionado(event.target.value);
  };

  const handleCargaChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCarga(event.target.value);
  };

  const handleFechaChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFecha(event.target.value);
  };

  // Nueva función para control de fardos por contrato
  const handleEnviar = async () => {
    try {
      setCargando(true);
      setMensaje('');

      // Verificar el tipo de producto seleccionado
      const productoSeleccionado = productos.find(p => p.id.toString() === producto);
      const esTipo3 = productoSeleccionado?.tipo === 3;

      if (esTipo3) {
        // Validaciones específicas para Producto Tipo 3
        if (!siloBolsaSeleccionado || !carga) {
          setMensaje('Por favor, complete los campos Silo Bolsa y Carga para el control');
          setTipoMensaje('error');
          return;
        }

        // Validar que la carga esté entre 1 y 100
        const cargaValue = parseInt(carga);
        if (cargaValue < 1 || cargaValue > 100) {
          setMensaje('La carga debe estar entre 1 y 100');
          setTipoMensaje('error');
          return;
        }

        // Para tipo 3, generar datos automáticamente en el control
        const siloBolsaEncontrado = silosBolsas.find(s => s.id.toString() === siloBolsaSeleccionado);
        const descripcionSilo = siloBolsaSeleccionado === '0' 
          ? 'GRANEL' 
          : siloBolsaEncontrado 
            ? `${siloBolsaEncontrado.numero_silobolsa}-${siloBolsaEncontrado.ubicacion}`
            : 'Silo Bolsa';

        const datosControl = [{
          id: siloBolsaSeleccionado === '0' ? 0 : parseInt(siloBolsaSeleccionado),
          descripcion: descripcionSilo,
          cantidad: parseInt(carga),
          fecha: new Date().toISOString().split('T')[0],
          aplicado: true,
          stock: 1,
          orden: 'NO',
          alta: true
        }];

        setAplicadas(datosControl);
        setMensaje('Datos de silo bolsa cargados en el control');
        setTipoMensaje('success');
        return;
      } else {
        // Validaciones para otros tipos de producto (Tipo 2 y otros)
        if (tipoOrden === 'C') {
          // Para órdenes de compra, solo validar desde y hasta
          if (!desde || !hasta) {
            setMensaje('Por favor, complete los campos desde y hasta para el control');
            setTipoMensaje('error');
            return;
          }
        } else {
          // Para otros tipos de orden, validar cliente, desde y hasta
          if (!desde || !hasta) {
            setMensaje('Por favor, complete los campos desde y hasta para el control');
            setTipoMensaje('error');
            return;
          }
        }

        // Ejecutar control de fardos solo para tipos que no sean 3
        if (contrato) {
          await handleControlFardosContrato();
        } else {
          await handleControl(true);
        }
        
        // Marcar que se realizó el primer envío para órdenes tipo "Baja"
        if (tipoOrden === 'B') {
          setPrimerEnvioRealizado(true);
        }
      }

    } catch (error) {
      console.error('Error al ejecutar control:', error);
      setMensaje('Error al ejecutar el control. Por favor, intente nuevamente.');
      setTipoMensaje('error');
    } finally {
      setCargando(false);
    }
  };

  // Nueva función para control de fardos por contrato
  const handleControlFardosContrato = async () => {
    try {
      setCargando(true);
      setMensaje('');

      if (!contrato || !producto || !desde || !hasta) {
        setMensaje('Por favor, complete los campos de contrato, producto, desde y hasta para el control');
        setTipoMensaje('error');
        return;
      }

      const token = await getAccessTokenSilently();
      const idEmpresa = user?.id_empresa;

      if (!idEmpresa) {
        setMensaje('No se encontró el ID de empresa en el usuario');
        setTipoMensaje('error');
        return;
      }
      
      
      const url = `${process.env.REACT_APP_ORDENES_URL}/control_fardos_contrato/${idEmpresa}/${contrato}/${producto}/${origen || '0'}/${cliente || '0'}/${desde}/${hasta}/`;
      
      
      console.log('Llamando a control de fardos contrato:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error en el control de fardos: ${response.status}`);
      }

      const controlData = await response.json();
      console.log('Datos de control de fardos recibidos:', controlData);
      
      // Filtrar fardos que ya están en el resumen
      const fardosNuevos = controlData.filter((item: any) => {
        const nroFardo = item.nro_fardo || item.numero_fardo || item.id;
        const yaProcesado = fardosProcesados.has(nroFardo);
        if (yaProcesado) {
          console.log(`Fardo ${nroFardo} ya está en el resumen, excluyendo del control`);
        }
        return !yaProcesado;
      });
      
      console.log(`Fardos nuevos (no duplicados): ${fardosNuevos.length} de ${controlData.length}`);
      
      // Detectar fardos duplicados solo entre los nuevos
      const fardosCount = new Map();
      console.log('Analizando duplicados entre fardos nuevos en handleControl...');
      
      fardosNuevos.forEach((item: any, index: number) => {
        const nroFardo = item.nro_fardo || item.numero_fardo || item.id;
        console.log(`Fardo ${index}: nro_fardo = ${nroFardo}, tipo = ${typeof nroFardo}`);
        
        if (nroFardo !== undefined && nroFardo !== null) {
          const currentCount = fardosCount.get(nroFardo) || 0;
          fardosCount.set(nroFardo, currentCount + 1);
          console.log(`Contador para fardo ${nroFardo}: ${currentCount + 1}`);
        }
      });
      
      console.log('Mapa de conteo de fardos nuevos en handleControl:', Object.fromEntries(fardosCount));
      
      // Formatear los datos para el sector Control (solo fardos nuevos)
      const datosControl = fardosNuevos.map((item: any, index: number) => {
        const nroFardo = item.nro_fardo || item.numero_fardo || item.id;
        const esDuplicado = fardosCount.get(nroFardo) > 1;
        // Normalizar el valor de aplicacion
        const aplicadoBackend = (typeof item.aplicacion === 'string' && item.aplicacion.trim().toUpperCase() === 'SI');
        const stockValue = item.stock === 'SI' || item.stock === true ? 1 : 0;
        
        // Usar el campo 'aceptado' que viene del backend en lugar de aplicar lógica propia
        const aceptadoBackend = item.aceptado === true || item.aceptado === 'true';
        
        return {
          id: nroFardo,
          id_fardo: item.id_fardo || null, // Guardar el id_fardo del backend
          descripcion: `${item.descripcion || `Fardo ${nroFardo}`}${esDuplicado ? ' (DUPLICADO)' : ''}`,
          cantidad: item.cantidad || 0,
          fecha: item.fecha || new Date().toISOString().split('T')[0],
          aplicado: aplicadoBackend,
          stock: stockValue,
          orden: item.orden || 'NO',
          nro_lote: item.nro_lote || null, // Guardar el nro_lote del backend
          alta: esDuplicado ? false : aceptadoBackend  // Usar el valor del backend
        };
      });
      
      console.log('Datos control finales en handleControl:', datosControl);
      setAplicadas(datosControl);
      
    //  if (showMessages) {
        // Contar duplicados para el mensaje
     //   const duplicados = Array.from(fardosCount.values()).filter(count => count > 1).length;
     //   const excluidos = controlData.length - fardosNuevos.length;
     //   console.log(`Total de fardos duplicados detectados en handleControl: ${duplicados}`);
     //   console.log(`Fardos excluidos por ya estar en resumen: ${excluidos}`);
        
     //   if (duplicados > 0 || excluidos > 0) {
     //     let mensajeFinal = 'Control realizado correctamente.';
     //     if (excluidos > 0) {
     //       mensajeFinal += ` Se excluyeron ${excluidos} fardos que ya están en el resumen.`;
     //     }
     //     if (duplicados > 0) {
     //       mensajeFinal += ` Se detectaron ${duplicados} fardos duplicados que fueron marcados como no aplicados.`;
     //     }
     //       setMensaje(mensajeFinal);
     //     } else {
     //       setMensaje('Control realizado correctamente');
     //     }
     //   setTipoMensaje('success');
     // }

    } catch (error) {
      console.error('Error en el control de fardos:', error);
      setMensaje('Error al realizar el control de fardos. Por favor, intente nuevamente.');
      setTipoMensaje('error');
    } finally {
      setCargando(false);
    }
  };

  const handleControl = async (showMessages = true) => {
    try {
      if (showMessages) {
        setCargando(true);
        setMensaje('');
      }

      // Validar campos según el tipo de orden
      if (tipoOrden === 'C') {
        // Para órdenes de compra, solo validar desde y hasta
        if (!desde || !hasta) {
          if (showMessages) {
            setMensaje('Por favor, complete los campos desde y hasta para el control');
            setTipoMensaje('error');
          }
          return;
        }
      } else {
        // Para otros tipos de orden, validar origen, cliente, desde y hasta
        if ( !desde || !hasta) {
          if (showMessages) {
            setMensaje('Por favor, complete los campos desde y hasta para el control');
            setTipoMensaje('error');
          }
          return;
        }
      }

      const token = await getAccessTokenSilently();
      
      // Construir URL según el tipo de orden
      let url;
      if (tipoOrden === 'C') {
        // Para órdenes de compra, no incluir origen ni cliente
        url = `${process.env.REACT_APP_API_URL}/ordenes/control/?desde=${desde}&hasta=${hasta}`;
      } else {
        // Para otros tipos de orden, incluir todos los parámetros
        url = `${process.env.REACT_APP_API_URL}/ordenes/control/?origen=${origen || '0'}&cliente=${cliente || '0'}&desde=${desde}&hasta=${hasta}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error en el control: ${response.status}`);
      }

      const controlData = await response.json();
      console.log('Datos de control recibidos:', controlData);
      
      // Filtrar fardos que ya están en el resumen
      const fardosNuevos = controlData.filter((item: any) => {
        const nroFardo = item.nro_fardo || item.numero_fardo || item.id;
        const yaProcesado = fardosProcesados.has(nroFardo);
        if (yaProcesado) {
          console.log(`Fardo ${nroFardo} ya está en el resumen, excluyendo del control`);
        }
        return !yaProcesado;
      });
      
      console.log(`Fardos nuevos (no duplicados): ${fardosNuevos.length} de ${controlData.length}`);
      
      // Detectar fardos duplicados solo entre los nuevos
      const fardosCount = new Map();
      console.log('Analizando duplicados entre fardos nuevos en handleControl...');
      
      fardosNuevos.forEach((item: any, index: number) => {
        const nroFardo = item.nro_fardo || item.numero_fardo || item.id;
        console.log(`Fardo ${index}: nro_fardo = ${nroFardo}, tipo = ${typeof nroFardo}`);
        
        if (nroFardo !== undefined && nroFardo !== null) {
          const currentCount = fardosCount.get(nroFardo) || 0;
          fardosCount.set(nroFardo, currentCount + 1);
          console.log(`Contador para fardo ${nroFardo}: ${currentCount + 1}`);
        }
      });
      
      console.log('Mapa de conteo de fardos nuevos en handleControl:', Object.fromEntries(fardosCount));
      
      // Formatear los datos para el sector Control (solo fardos nuevos)
      const datosControl = fardosNuevos.map((item: any, index: number) => {
        const nroFardo = item.nro_fardo || item.numero_fardo || item.id;
        const esDuplicado = fardosCount.get(nroFardo) > 1;
        // Normalizar el valor de aplicacion
        const aplicadoBackend = (typeof item.aplicacion === 'string' && item.aplicacion.trim().toUpperCase() === 'SI');
        const stockValue = item.stock === 'SI' || item.stock === true ? 1 : 0;
        
        // Usar el campo 'aceptado' que viene del backend en lugar de aplicar lógica propia
        const aceptadoBackend = item.aceptado === true || item.aceptado === 'true';
        
        return {
          id: nroFardo,
          id_fardo: item.id_fardo || null, // Guardar el id_fardo del backend
          descripcion: `${item.descripcion || `Fardo ${nroFardo}`}${esDuplicado ? ' (DUPLICADO)' : ''}`,
          cantidad: item.cantidad || 0,
          fecha: item.fecha || new Date().toISOString().split('T')[0],
          aplicado: aplicadoBackend,
          stock: stockValue,
          orden: item.orden || 'NO',
          nro_lote: item.nro_lote || null, // Guardar el nro_lote del backend
          alta: esDuplicado ? false : aceptadoBackend  // Usar el valor del backend
        };
      });
      
      console.log('Datos control finales en handleControl:', datosControl);
      setAplicadas(datosControl);
      
      if (showMessages) {
        // Contar duplicados para el mensaje
        const duplicados = Array.from(fardosCount.values()).filter(count => count > 1).length;
        const excluidos = controlData.length - fardosNuevos.length;
        console.log(`Total de fardos duplicados detectados en handleControl: ${duplicados}`);
        console.log(`Fardos excluidos por ya estar en resumen: ${excluidos}`);
        
        if (duplicados > 0 || excluidos > 0) {
          let mensajeFinal = 'Control realizado correctamente.';
          if (excluidos > 0) {
            mensajeFinal += ` Se excluyeron ${excluidos} fardos que ya están en el resumen.`;
          }
          if (duplicados > 0) {
            mensajeFinal += ` Se detectaron ${duplicados} fardos duplicados que fueron marcados como no aplicados.`;
          }
          setMensaje(mensajeFinal);
        } else {
          setMensaje('Control realizado correctamente');
        }
        setTipoMensaje('success');
      }

    } catch (error) {
      console.error('Error en el control:', error);
      if (showMessages) {
        setMensaje('Error al realizar el control. Por favor, intente nuevamente.');
        setTipoMensaje('error');
      }
    } finally {
      if (showMessages) {
        setCargando(false);
      }
    }
  };

  // Función para manejar cambios en el checkbox de aplicación
  const handleAplicacionChange = (id: number, aplicado: boolean) => {
    // Validaciones según el tipo de orden
    const item = aplicadas.find(a => a.id === id);
    if (!item) return;

    // Para órdenes tipo "C" (Compra): solo se pueden tildar fardos que estén en Aplicación
    if (tipoOrden === 'C') {
      if (aplicado && !item.aplicado) {
        // Intentando tildar un fardo que no está en aplicación
        setMensaje('Para órdenes de Compra, solo se pueden seleccionar fardos que estén en Aplicación');
        setTipoMensaje('error');
        return;
      }
    }

    // Para órdenes tipo "V" (Venta): solo se pueden tildar fardos que estén en Stock
    if (tipoOrden === 'V') {
      if (aplicado && (item.stock || 0) <= 0) {
        // Intentando tildar un fardo que no está en stock
        setMensaje('Para órdenes de Venta, solo se pueden seleccionar fardos que estén en Stock');
        setTipoMensaje('error');
        return;
      }
    }

    const updatedAplicadas = aplicadas.map((item) =>
      item.id === id ? { ...item, aplicado } : item
    );
    setAplicadas(updatedAplicadas);
    
    // Opcional: Mostrar mensaje de confirmación
    console.log(`Fardo ${id} marcado como ${aplicado ? 'aplicado' : 'no aplicado'}`);
  };

  // Función para tildar todos los elementos del control
  const handleTildarTodos = () => {
    const updatedAplicadas = aplicadas.map((item) => {
      let shouldApply = true;

      // Para órdenes tipo "C" (Compra): solo tildar fardos que estén en Aplicación
      if (tipoOrden === 'C') {
        shouldApply = item.aplicado === true;
      }

      // Para órdenes tipo "V" (Venta): solo tildar fardos que estén en Stock
      if (tipoOrden === 'V') {
        shouldApply = (item.stock || 0) > 0;
      }

      return {
        ...item,
        aplicado: shouldApply
      };
    });
    setAplicadas(updatedAplicadas);
    
    // Mostrar mensaje informativo
    if (tipoOrden === 'C') {
      setMensaje('Se han marcado solo los fardos que están en Aplicación (requerido para órdenes de Compra)');
      setTipoMensaje('success');
    } else if (tipoOrden === 'V') {
      setMensaje('Se han marcado solo los fardos que están en Stock (requerido para órdenes de Venta)');
      setTipoMensaje('success');
    } else {
      console.log('Todos los fardos han sido marcados como aplicados');
    }
  };

  // Función para destildar todos los elementos del control
  const handleDestildarTodos = () => {
    const updatedAplicadas = aplicadas.map((item) => ({
      ...item,
      aplicado: false
    }));
    setAplicadas(updatedAplicadas);
    console.log('Todos los fardos han sido desmarcados');
  };

  // Función para el botón Resumir
  const handleResumir = () => {
    try {
      // Verificar que hay datos en el control
      if (aplicadas.length === 0) {
        setMensaje('No hay datos de control para resumir. Ejecute el control primero.');
        setTipoMensaje('error');
        return;
      }

      // Verificar el tipo de producto seleccionado
      const productoSeleccionado = productos.find(p => p.id.toString() === producto);
      const esTipo3 = productoSeleccionado?.tipo === 3;

      if (esTipo3) {
        // Lógica específica para Producto Tipo 3
        // Filtrar solo los silos que tienen checkbox marcado (aplicado = true)
        const silosMarcados = aplicadas.filter(item => item.aplicado);
        
        if (silosMarcados.length === 0) {
          setMensaje('No hay silos marcados para resumir. Marque al menos un silo.');
          setTipoMensaje('error');
          return;
        }

        // Contar los datos del control actual (solo los marcados)
        const totalSilos = silosMarcados.length;
        const totalCargas = silosMarcados.reduce((total, item) => total + (item.cantidad || 0), 0);
        const aplicados = silosMarcados.filter(item => item.aplicado).length;

        // Crear el resumen acumulando con los datos existentes
        const resumenData = {
          totalSilos: (resumen?.totalSilos || 0) + totalSilos,
          totalCargas: (resumen?.totalCargas || 0) + totalCargas,
          aplicados: (resumen?.aplicados || 0) + aplicados,
          noAplicados: (resumen?.noAplicados || 0) + (totalSilos - aplicados),
          fecha: new Date().toISOString().split('T')[0],
          contrato,
          siloBolsa: siloBolsaSeleccionado,
          carga,
          silosDetalle: [
            ...(resumen?.silosDetalle || []),
            ...silosMarcados.map(item => ({
              nroSilo: item.id,
              descripcion: item.descripcion,
              carga: item.cantidad,
              aplicado: item.aplicado
            }))
          ]
        };

        console.log('Resumen acumulado Tipo 3:', resumenData);
        
        // Guardar el resumen acumulado en el estado
        setResumen(resumenData);
        
        // Agregar los silos procesados a la matriz de silos ya procesados
        const nuevosSilosProcesados = new Set(fardosProcesados); // Reutilizamos la misma variable
        silosMarcados.forEach(item => {
          nuevosSilosProcesados.add(item.id);
        });
        setFardosProcesados(nuevosSilosProcesados);
        
        // Limpiar los datos del control después de resumir
        setAplicadas([]);
        
        // Limpiar los campos específicos del tipo 3
        setSiloBolsaSeleccionado('');
        setCarga('');
        
        // Mostrar mensaje de éxito
        setMensaje(`Resumen generado correctamente. ${totalSilos} silos procesados con ${totalCargas} cargas totales.`);
        setTipoMensaje('success');
        
      } else {
        // Lógica para otros tipos de producto (Tipo 2 y otros)
        // Filtrar solo los fardos que tienen checkbox marcado (true) Y están habilitados (orden === 'NO')
        const fardosMarcados = aplicadas.filter(item => item.aplicado && item.orden === 'NO');
        
        // Contar los datos del control actual (solo los marcados)
        const totalFardos = fardosMarcados.length;
        const aplicados = fardosMarcados.filter(item => item.aplicado).length;
        const noAplicados = fardosMarcados.filter(item => !item.aplicado).length;
        const conStock = fardosMarcados.filter(item => (item.stock || 0) > 0).length;
        const sinStock = fardosMarcados.filter(item => (item.stock || 0) === 0).length;

        // Crear el resumen acumulando con los datos existentes
        const resumenData = {
          totalFardos: (resumen?.totalFardos || 0) + totalFardos,
          aplicados: (resumen?.aplicados || 0) + aplicados,
          noAplicados: (resumen?.noAplicados || 0) + noAplicados,
          conStock: (resumen?.conStock || 0) + conStock,
          sinStock: (resumen?.sinStock || 0) + sinStock,
          fecha: new Date().toISOString().split('T')[0],
          contrato,
          cliente,
          desde,
          hasta,
          fardosDetalle: [
            ...(resumen?.fardosDetalle || []),
            ...fardosMarcados.map(item => ({
              nroFardo: item.id,
              id_fardo: item.id_fardo, // Guardar el id_fardo
              id_orden: item.id_orden, // Guardar el id_orden
              nro_lote: item.nro_lote, // Guardar el nro_lote
              cliente, // Guardar el cliente para cada fardo
              origen, // Guardar el origen para cada fardo
              aplicado: item.aplicado,
              stock: item.stock,
              orden: item.orden // Guardar el estado de orden
            }))
          ]
        };

        console.log('Resumen acumulado con datos completos:', resumenData);
        console.log('Fardos detalle:', resumenData.fardosDetalle);
        
        // Guardar el resumen acumulado en el estado
        setResumen(resumenData);
        
        // Agregar los fardos procesados a la matriz de fardos ya procesados
        const nuevosFardosProcesados = new Set(fardosProcesados);
        fardosMarcados.forEach(item => {
          nuevosFardosProcesados.add(item.id);
        });
        setFardosProcesados(nuevosFardosProcesados);
        
        // Limpiar los datos del control después de resumir
        setAplicadas([]);
        
        // Limpiar los campos desde y hasta
        setDesde('');
        setHasta('');
        
        // Mostrar mensaje de éxito
        setMensaje('Resumen generado correctamente. Los datos del control han sido procesados.');
        setTipoMensaje('success');
      }
      
    } catch (error) {
      console.error('Error al generar resumen:', error);
      setMensaje('Error al generar el resumen');
      setTipoMensaje('error');
    }
  };

  // Función para verificar si el botón Grabar debe estar habilitado
  const isGrabarEnabled = () => {
    const productoSeleccionado = productos.find(p => p.id.toString() === producto);
    const esTipo3 = productoSeleccionado?.tipo === 3;

    if (esTipo3) {
      // Para Producto Tipo 3: habilitar solo si Total Cargas en Resumen = 100
      return resumen && resumen.totalCargas === 100;
    } else {
      // Para otros tipos: habilitar si hay resumen
      return resumen;
    }
  };

  // Función para el botón Grabar
  const handleGrabar = async () => {
    try {
      setGrabarCargando(true);
      setMensaje('');

      // Verificar el tipo de producto seleccionado
      const productoSeleccionado = productos.find(p => p.id.toString() === producto);
      const esTipo3 = productoSeleccionado?.tipo === 3;

      if (esTipo3) {
        // Debug: Log de valores para depuración
        console.log('Debug - Valores de campos Tipo 3:', {
          tipoOrden,
          producto,
          destinatario,
          empresa,
          direccion,
          provincia,
          localidad,
          contrato,
          resumen: resumen ? {
            totalSilos: resumen.totalSilos,
            totalCargas: resumen.totalCargas,
            aplicados: resumen.aplicados
          } : null
        });

        // Validaciones específicas para Producto Tipo 3
        if (!tipoOrden || !producto || !destinatario || !empresa || !direccion || !provincia || !localidad) {
          const camposFaltantes = [];
          if (!tipoOrden) camposFaltantes.push('Tipo Orden');
          if (!producto) camposFaltantes.push('Producto');
          if (!destinatario) camposFaltantes.push('Destinatario');
          if (!empresa) camposFaltantes.push('Empresa destino');
          if (!direccion) camposFaltantes.push('Dirección');
          if (!provincia) camposFaltantes.push('Provincia');
          if (!localidad) camposFaltantes.push('Localidad');
          
          setMensaje(`Por favor, complete los siguientes campos requeridos: ${camposFaltantes.join(', ')}.`);
          setTipoMensaje('error');
          return;
        }

        // Validar que el Total Cargas en el Resumen sea exactamente 100
        if (!resumen || resumen.totalCargas !== 100) {
          setMensaje('Para grabar una orden de Producto Tipo 3, el Total Cargas en el Resumen debe ser exactamente 100.');
          setTipoMensaje('error');
          return;
        }
      } else {
        // Validaciones para otros tipos de producto (Tipo 2 y otros)
        // Verificar que hay datos en el resumen
        if (!resumen || !resumen.fardosDetalle || resumen.fardosDetalle.length === 0) {
          setMensaje('No hay datos de resumen para grabar. Ejecute el control y resuma primero.');
          setTipoMensaje('error');
          return;
        }

        // Verificar campos requeridos
        if (!tipoOrden || !producto || !destinatario || !empresa || !direccion || !provincia || !localidad) {
          setMensaje('Por favor, complete todos los campos requeridos: Tipo Orden, Producto, Destinatario, Empresa destino, Dirección, Provincia y Localidad.');
          setTipoMensaje('error');
          return;
        }
      }

      const token = await getAccessTokenSilently();
      const idEmpresa = user?.id_empresa;
      const idSucursal = user?.id_sucursal;
      
      if (!idEmpresa) {
        setMensaje('No se encontró el ID de empresa en el usuario');
        setTipoMensaje('error');
        return;
      }

      if (!idSucursal) {
        setMensaje('No se encontró el ID de sucursal en el usuario');
        setTipoMensaje('error');
        return;
      }

      // Obtener datos del contrato seleccionado si existe
      let compradorId: number | null = null;
      let vendedorId: number | null = null;
      
      if (contrato) {
        const contratoSeleccionado = contratos.find(c => c.id.toString() === contrato);
        if (contratoSeleccionado) {
          // Para órdenes de Venta (V), el comprador_id debe ser el comprador del contrato
          // Para órdenes de Compra (C), el comprador_id debe ser el comprador del contrato
          compradorId = contratoSeleccionado.comprador_id;
          vendedorId = contratoSeleccionado.vendedor_id;
          
          console.log('Datos del contrato:', {
            tipo_contrato: contratoSeleccionado.tipo,
            tipo_orden: tipoOrden,
            comprador_id: compradorId,
            vendedor_id: vendedorId,
            comprador_nombre: contratoSeleccionado.comprador_nombre,
            vendedor_nombre: contratoSeleccionado.vendedor_nombre,
            contrato: contratoSeleccionado
          });
          
          // Validar que comprador_id y vendedor_id no sean iguales
          if (compradorId === vendedorId) {
            console.warn('ADVERTENCIA: comprador_id y vendedor_id son iguales:', {
              comprador_id: compradorId,
              vendedor_id: vendedorId,
              tipo_contrato: contratoSeleccionado.tipo
            });
          }
        } else {
          console.error('No se encontró el contrato seleccionado:', contrato);
        }
      } else {
        console.log('No hay contrato seleccionado, comprador_id será null');
      }

      // Validar que existan los datos necesarios del contrato para Tipo 3
      if (esTipo3 && !vendedorId) {
        setMensaje('Error: No se encontró el vendedor del contrato. Verifique que el contrato tenga datos válidos.');
        setTipoMensaje('error');
        return;
      }

      console.log('Valores finales antes de enviar:', {
        compradorId,
        vendedorId,
        contrato,
        tipoOrden
      });

      // Preparar datos para enviar al backend
      let ordenData;
      let ordenDetalle: any[] = [];
      
      if (esTipo3) {
        // Preparar detalles de orden para Producto Tipo 3
        ordenDetalle = resumen.silosDetalle.map((silo: any) => ({
          // Campos que van en 0 para Tipo 3
          kilosbolson: 0,
          id_fardo: 0,
          nro_fardo: 0,
          nro_lote: 0,
          
          // Campos específicos de silo bolsa
          nbolson: silo.nroSilo, // id_silobolsa
          partesilobolsa: silo.carga, // carga
          
          // Campos estándar
          alta: true,
          empresa_id: idEmpresa,
          orden_id: 0, // Se asignará en el backend
          origen_id: vendedorId || 0, // Usar vendedor_id del contrato
          cliente_id: cliente && cliente !== '' ? parseInt(cliente) : 0, // Usar cliente_id en lugar de destinatario
          sucursal_id: idSucursal
        }));

        // Datos específicos para Producto Tipo 3
        ordenData = {
          tipo: tipoOrden,
          producto: parseInt(producto),
          fecha,
          contrato: contrato ? parseInt(contrato) : 0,
          comprador: compradorId,
          vendedor: vendedorId,
          destino: parseInt(destinatario),
          origen_id: parseInt(origen) || 0,
          cliente_id: parseInt(cliente) || 0,
          empresa: empresa || '',
          direccion: direccion || '',
          provincia: provincia ? parseInt(provincia) : null,
          localidad: localidad ? parseInt(localidad) : null,
          total_cargas: resumen.totalCargas,
          total_silos: resumen.totalSilos,
          orden_detalle: ordenDetalle,
          fardos: [] // Para tipo 3 no hay fardos
        };
      } else {
        // Datos para otros tipos de producto (Tipo 2 y otros)
        ordenData = {
          tipo: tipoOrden,
          producto: parseInt(producto),
          fecha: new Date().toISOString().split('T')[0],
          contrato: contrato ? parseInt(contrato) : 0,
          comprador: compradorId,
          vendedor: vendedorId,
          destino: parseInt(destinatario),
          origen_id: parseInt(origen) || 0,
          cliente_id: parseInt(cliente) || 0,
          empresa: empresa || '',
          direccion: direccion || '',
          provincia: provincia ? parseInt(provincia) : null,
          localidad: localidad ? parseInt(localidad) : null,
          fardos: resumen.fardosDetalle.map((fardo: any) => ({
            id: fardo.nroFardo,
            id_fardo: fardo.id_fardo || 0,
            id_orden: fardo.id_orden || 0, // Enviar el id_orden del backend
            origen_id: fardo.origen ? parseInt(fardo.origen) : (origen ? parseInt(origen) : 0), // Usar origen específico del fardo o el general
            cliente_id: fardo.cliente ? parseInt(fardo.cliente) : (cliente ? parseInt(cliente) : 0), // Usar cliente específico del fardo o el general
            nro_lote: fardo.nro_lote || 0, // Enviar el nro_lote del backend
            nbolson: 0,  // Valor por defecto, ajustar según necesidad
            partebolson: 0, // Valor por defecto, ajustar según necesidad
            kilosbolson: 0 // Valor por defecto, ajustar según necesidad
          }))
        };
      }

      console.log('Datos a enviar al backend:', ordenData);
      console.log('Verificación del comprador_id enviado:', {
        comprador_id_en_ordenData: ordenData.comprador,
        comprador_id_original: compradorId,
        contrato_seleccionado: contrato
      });

      if (esTipo3) {
        console.log('Detalles de orden Tipo 3:', ordenDetalle);
        console.log('Valores utilizados para Tipo 3:', {
          vendedorId,
          cliente,
          idEmpresa,
          idSucursal
        });
      } else {
        console.log('Detalles de fardos a enviar:', ordenData.fardos);
        console.log('Resumen completo:', resumen);
        console.log('=== DETALLE COMPLETO DE FARDOS A GRABAR ===');
        resumen.fardosDetalle.forEach((fardo: any, index: number) => {
          console.log(`Fardo ${index + 1}:`, {
            nroFardo: fardo.nroFardo,
            id_fardo: fardo.id_fardo,
            nro_lote: fardo.nro_lote,
            cliente: fardo.cliente,
            origen: fardo.origen,
            aplicado: fardo.aplicado,
            stock: fardo.stock,
            orden: fardo.orden
          });
        });
        console.log('=== FIN DETALLE DE FARDOS ===');
        console.log('=== FORMATO FINAL ENVIADO AL BACKEND ===');
        ordenData.fardos.forEach((fardo: any, index: number) => {
          console.log(`Fardo ${index + 1} (formato backend):`, {
            id: fardo.id,
            id_fardo: fardo.id_fardo,
            origen_id: fardo.origen_id,
            cliente_id: fardo.cliente_id,
            nro_lote: fardo.nro_lote,
            nbolson: fardo.nbolson,
            partebolson: fardo.partebolson,
            kilosbolson: fardo.kilosbolson
          });
        });
        console.log('=== FIN FORMATO BACKEND ===');
      }

      // Llamar a la API del backend
      const url = `${process.env.REACT_APP_ORDENES_URL}/alta_ordenes/${idEmpresa}/${idSucursal}/`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ordenData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Respuesta del backend:', result);

      // Mostrar mensaje de éxito
      if (esTipo3) {
        setMensaje(`Orden de silo bolsa creada exitosamente. Número de orden: ${result.nro_orden}`);
      } else {
        setMensaje(`Orden creada exitosamente. Número de orden: ${result.nro_orden}. Fardos procesados: ${result.fardos_procesados}`);
      }
      setTipoMensaje('success');

      // Limpiar el resumen después de grabar exitosamente
      setResumen(null);
      setFardosProcesados(new Set());

      // Opcional: Limpiar otros campos del formulario
      setTipoOrden('');
      setContrato('');
      setProducto('');
      setDestinatario('');
      setEntregador('');
      setEmpresa('');
      setDireccion('');
      setProvincia('');
      setLocalidad('');
      setOrigen('');
      setCliente('');
      setSiloBolsaSeleccionado('');
      setCarga('');

    } catch (error: any) {
      console.error('Error al grabar orden:', error);
      setMensaje(`Error al grabar la orden: ${error.message || 'Error desconocido'}`);
      setTipoMensaje('error');
    } finally {
      setGrabarCargando(false);
    }
  };

  return (
    <div className="container" style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <style>
        {`
          .sticky-header {
            position: sticky !important;
            top: 0 !important;
            background-color: ${theme.colors.backgroundGray} !important;
            z-index: 1000 !important;
            border-bottom: 2px solid ${theme.colors.border} !important;
          }
          .sticky-header th {
            background-color: ${theme.colors.backgroundGray} !important;
            border-bottom: 2px solid ${theme.colors.border} !important;
            padding: 8px 10px !important;
            font-weight: 600 !important;
            font-size: 12px !important;
            color: ${theme.colors.textPrimary} !important;
          }
          .control-table-container {
            max-height: 400px;
            overflow-y: auto;
            border: 1px solid ${theme.colors.border};
            border-radius: ${theme.styles.borderRadius};
          }
          .control-table td {
            padding: 8px 10px !important;
            font-size: 12px !important;
            border-bottom: 1px solid ${theme.colors.borderLight} !important;
          }
          .control-table tr:hover {
            background-color: ${theme.colors.backgroundGray} !important;
          }
          .table-sm th, .table-sm td {
            padding: 8px 10px !important;
            font-size: 12px !important;
          }
          .control-table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
          }
          .tipo-orden-select {
            background-color: ${theme.colors.backgroundGray} !important;
            border-color: ${theme.colors.border} !important;
          }
          .tipo-orden-select option {
            background-color: ${theme.colors.background} !important;
          }
          .tipo-orden-select:focus {
            border-color: ${theme.colors.primary} !important;
            box-shadow: 0 0 0 2px rgba(45, 92, 146, 0.2) !important;
          }
          .form-control {
            border-color: ${theme.colors.border} !important;
            transition: all 0.3s ease !important;
          }
          .form-control:focus {
            border-color: ${theme.colors.primary} !important;
            box-shadow: 0 0 0 2px rgba(45, 92, 146, 0.2) !important;
          }
          .btn-grabar {
            transition: all 0.3s ease !important;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
            border: 1px solid ${theme.colors.secondary} !important;
            background-color: ${theme.colors.secondary} !important;
            color: ${theme.colors.textLight} !important;
            font-weight: 600 !important;
          }
          .btn-grabar:hover:not(:disabled) {
            transform: translateY(-1px) !important;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
            background-color: ${theme.colors.secondaryBright} !important;
            border-color: ${theme.colors.secondaryBright} !important;
            color: ${theme.colors.textLight} !important;
          }
          .btn-grabar:active:not(:disabled) {
            transform: translateY(0) !important;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
          }
          .btn-grabar:disabled {
            opacity: 0.5 !important;
            cursor: not-allowed !important;
            background-color: ${theme.colors.border} !important;
            border-color: ${theme.colors.border} !important;
          }
          .card {
            border: 1px solid ${theme.colors.border} !important;
          }
          .card-body {
            background-color: ${theme.colors.background} !important;
          }
        `}
      </style>
      
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '16px',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <h3 style={{ 
          fontSize: '22px', 
          fontWeight: 600, 
          margin: 0, 
          color: theme.colors.primaryDark 
        }}>
          Órdenes [Despachos/Recepción]
        </h3>
      </div>

      {/* Mensaje de estado */}
      {mensaje && (
        <div 
          className="mb-3" 
          style={{ 
            padding: '12px 16px', 
            fontSize: '13px',
            borderRadius: theme.styles.borderRadius,
            backgroundColor: tipoMensaje === 'success' ? '#f6ffed' : '#fff2f0',
            border: `1px solid ${tipoMensaje === 'success' ? theme.colors.secondary : theme.colors.error}`,
            color: tipoMensaje === 'success' ? theme.colors.textPrimary : theme.colors.error
          }}
        >
          {mensaje}
        </div>
      )}

      {/* Indicador de carga */}
      {cargando && (
        <div className="text-center mb-3" style={{ padding: '20px' }}>
          <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}>
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      )}
      
      <div className="row" style={{ marginBottom: '12px' }}>
        <div className="col-md-4">
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label htmlFor="tipoOrden" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Tipo Orden *</label>
            <select
              id="tipoOrden"
              className="form-control tipo-orden-select"
              value={tipoOrden}
              onChange={handleTipoOrdenChange}
              required
              style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
            >
              <option value="">Seleccione un tipo</option>
              {tiposOrden.map((tipo) => (
                <option key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label htmlFor="contrato" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Contrato</label>
            <select
              id="contrato"
              className="form-control"
              value={contrato}
              onChange={handleContratoChange}
              disabled={!tipoOrden || (tipoOrden !== 'C' && tipoOrden !== 'V')}
              style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
            >
              <option value="">
                {!tipoOrden 
                  ? 'Seleccione primero un tipo de orden' 
                  : (tipoOrden !== 'C' && tipoOrden !== 'V') 
                    ? 'No disponible para este tipo de orden'
                    : 'Seleccione un contrato'
                }
              </option>
              {contratos.map((contrato) => (
                <option key={contrato.id} value={contrato.id}>
                  {contrato.ncontrato}
                </option>
              ))}
            </select>
            {tipoOrden && tipoOrden !== 'C' && tipoOrden !== 'V' && (
              <small className="text-muted" style={{ fontSize: '11px' }}>
                Los contratos no están disponibles para este tipo de orden
              </small>
            )}
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label htmlFor="producto" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Producto *</label>
            <select
              id="producto"
              className="form-control"
              value={producto}
              onChange={handleProductoChange}
              required
              disabled={!!contrato}
              style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
            >
              <option value="">
                {contrato 
                  ? 'Seleccionado automáticamente del contrato' 
                  : 'Seleccione un producto'
                }
              </option>
              {productos.map((prod) => (
                <option key={prod.id} value={prod.id}>
                  {prod.descripcion}
                </option>
              ))}
            </select>
            {contrato && (
              <small className="text-muted" style={{ fontSize: '11px' }}>
                El producto se determina automáticamente según el contrato seleccionado
              </small>
            )}
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label htmlFor="fecha" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Fecha *</label>
            <input
              type="date"
              id="fecha"
              className="form-control"
              value={fecha}
              onChange={handleFechaChange}
              required
              style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label htmlFor="comprador" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Comprador</label>
            <input
              type="text"
              id="comprador"
              className="form-control"
              value={contrato ? (contratos.find(c => c.id.toString() === contrato)?.comprador_nombre || '') : ''}
              disabled
              placeholder="Seleccione un contrato"
              style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label htmlFor="vendedor" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Vendedor</label>
            <input
              type="text"
              id="vendedor"
              className="form-control"
              value={contrato ? (contratos.find(c => c.id.toString() === contrato)?.vendedor_nombre || '') : ''}
              disabled
              placeholder="Seleccione un contrato"
              style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
            />
          </div>
        </div>
      </div>

      <div className="row" style={{ marginBottom: '12px' }}>
        <div className="col-md-4">
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label htmlFor="destinatario" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Destinatario *</label>
            <select
              id="destinatario"
              className="form-control"
              value={destinatario}
              onChange={handleDestinatarioChange}
              required
              disabled={!!contrato}
              style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
            >
              <option value="">
                {contrato 
                  ? 'Seleccionado automáticamente del contrato' 
                  : 'Seleccione un destinatario'
                }
              </option>
              {destinatarios.map((dest) => (
                                        <option key={dest.id} value={dest.id}>
                  {dest.nc}-{dest.denominacion}
                </option>
              ))}
            </select>
            {contrato && (
              <small className="text-muted" style={{ fontSize: '11px' }}>
                El destinatario se determina automáticamente según el contrato seleccionado
              </small>
            )}
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label htmlFor="entregador" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Entregador</label>
            <select
              id="entregador"
              className="form-control"
              value={entregador}
              onChange={handleEntregadorChange}
              disabled={!!contrato}
              style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
            >
              <option value="">
                {contrato 
                  ? 'Seleccionado automáticamente del contrato' 
                  : 'Seleccione un entregador'
                }
              </option>
              {entregadores.map((rec) => (
                                        <option key={rec.id} value={rec.id}>
                  {rec.nc}-{rec.denominacion}
                </option>
              ))}
            </select>
            {contrato && (
              <small className="text-muted" style={{ fontSize: '11px' }}>
                El entregador se determina automáticamente según el contrato seleccionado
              </small>
            )}
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label htmlFor="empresa" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Empresa destino *</label>
            <input
              type="text"
              id="empresa"
              className="form-control"
              value={empresa}
              onChange={handleEmpresaChange}
              placeholder="Nombre de la empresa destino"
              required
              style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
            />
          </div>
        </div>
      </div>

      <div className="row" style={{ marginBottom: '12px' }}>
        <div className="col-md-4">
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label htmlFor="direccion" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Dirección *</label>
            <input
              type="text"
              id="direccion"
              className="form-control"
              value={direccion}
              onChange={handleDireccionChange}
              placeholder="Dirección completa"
              required
              style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label htmlFor="provincia" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Provincia *</label>
            <select
              id="provincia"
              className="form-control"
              value={provincia}
              onChange={handleProvinciaChange}
              required
              style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
            >
              <option value="">Seleccione una provincia</option>
              {provincias.map((prov) => (
                <option key={prov.id} value={prov.id}>
                  {prov.provincia}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label htmlFor="localidad" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Localidad *</label>
            <select
              id="localidad"
              className="form-control"
              value={localidad}
              onChange={handleLocalidadChange}
              disabled={!provincia}
              required
              style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
            >
              <option value="">Seleccione una localidad</option>
              {localidades.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.localidad}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <hr style={{ height: '2px', margin: '16px 0', backgroundColor: theme.colors.primary, border: 'none' }} />

      <div className="row" style={{ marginBottom: '16px' }}>
        <div className="col-md-4">
          <div className="card h-100" style={{ marginBottom: '16px', borderRadius: theme.styles.borderRadius, boxShadow: theme.styles.boxShadowLight }}>
            <div className="card-header text-white text-center" style={{ backgroundColor: theme.colors.primaryDark, padding: '10px 12px', fontSize: '14px', fontWeight: 600, borderRadius: `${theme.styles.borderRadius} ${theme.styles.borderRadius} 0 0` }}>
              <h5 className="m-0" style={{ fontSize: '14px', fontWeight: 600 }}>Selección</h5>
            </div>
            <div className="card-body" style={{ padding: '12px' }}>
              {/* Campos específicos para Producto Tipo 3 */}
              {(() => {
                const productoSeleccionado = productos.find(p => p.id.toString() === producto);
                const esTipo3 = productoSeleccionado?.tipo === 3;
                
                if (esTipo3) {
                  return (
                    <>
                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label htmlFor="siloBolsa" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Silo Bolsa *</label>
                        <select
                          id="siloBolsa"
                          className="form-control"
                          value={siloBolsaSeleccionado}
                          onChange={handleSiloBolsaChange}
                          required
                          style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
                        >
                          <option value="">Seleccione silo bolsa</option>
                          <option value="0">GRANEL</option>
                          {silosBolsas.map((silo) => (
                            <option key={silo.id} value={silo.id}>
                              {silo.numero_silobolsa}-{silo.ubicacion}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label htmlFor="carga" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Carga *</label>
                        <input
                          type="number"
                          id="carga"
                          className="form-control"
                          value={carga}
                          onChange={handleCargaChange}
                          placeholder="1/100"
                          min="1"
                          max="100"
                          required
                          style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
                        />
                        <small className="text-muted" style={{ fontSize: '11px' }}>
                          Valor entre 1 y 100
                        </small>
                      </div>
                    </>
                  );
                }

                // Campos para otros tipos de producto (Tipo 2 y otros)
                return (
                  <>
                    <div className="form-group" style={{ marginBottom: '12px' }}>
                      <label htmlFor="origen" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Origen *</label>
                      <select
                        id="origen"
                        className="form-control"
                        value={origen}
                        onChange={(e) => setOrigen(e.target.value)}
                        required
                        disabled={tipoOrden === 'C' || (tipoOrden === 'B' && primerEnvioRealizado) || !!resumen}
                        style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
                      >
                        <option value="">
                          {tipoOrden === 'C' 
                            ? 'No disponible para órdenes de Compra' 
                            : (tipoOrden === 'B' && primerEnvioRealizado)
                              ? 'Bloqueado después del primer envío'
                              : !!resumen
                                ? 'Bloqueado - Información ya resumida'
                                : 'Seleccione origen'
                          }
                        </option>
                        {origenes.map((orig) => (
                          <option key={orig.id} value={orig.id}>
                            {orig.nc}-{orig.denominacion}
                          </option>
                        ))}
                      </select>
                      {tipoOrden === 'C' && (
                        <small className="text-muted" style={{ fontSize: '11px' }}>
                          El origen no es necesario para órdenes de compra
                        </small>
                      )}
                      {tipoOrden === 'B' && primerEnvioRealizado && (
                        <small className="text-muted" style={{ fontSize: '11px' }}>
                          Origen bloqueado después del primer envío
                        </small>
                      )}
                      {!!resumen && (
                        <small className="text-muted" style={{ fontSize: '11px' }}>
                          Origen bloqueado - La información ya ha sido resumida
                        </small>
                      )}
                    </div>

                    <div className="form-group" style={{ marginBottom: '12px' }}>
                      <label htmlFor="cliente" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Cliente *</label>
                      <select
                        id="cliente"
                        className="form-control"
                        value={cliente}
                        onChange={(e) => setCliente(e.target.value)}
                        required
                        disabled={tipoOrden === 'C' || (tipoOrden === 'V' && contrato !== '') || (tipoOrden === 'B' && primerEnvioRealizado)}
                        style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
                      >
                        <option value="">
                          {tipoOrden === 'C' 
                            ? 'No disponible para órdenes de Compra' 
                            : (tipoOrden === 'V' && contrato !== '')
                              ? 'Seleccionado automáticamente del contrato'
                              : (tipoOrden === 'B' && primerEnvioRealizado)
                                ? 'Bloqueado después del primer envío'
                                : 'Seleccione cliente'
                          }
                        </option>
                        {clientes.map((cli) => (
                          <option key={cli.id} value={cli.id}>
                            {cli.nc}-{cli.denominacion}
                          </option>
                        ))}
                      </select>
                      {tipoOrden === 'C' && (
                        <small className="text-muted" style={{ fontSize: '11px' }}>
                          El cliente no es necesario para órdenes de compra
                        </small>
                      )}
                      {tipoOrden === 'V' && contrato !== '' && (
                        <small className="text-muted" style={{ fontSize: '11px' }}>
                          El cliente se determina automáticamente según el contrato de venta
                        </small>
                      )}
                      {tipoOrden === 'B' && primerEnvioRealizado && (
                        <small className="text-muted" style={{ fontSize: '11px' }}>
                          Cliente bloqueado después del primer envío
                        </small>
                      )}
                    </div>

                    <div className="form-group" style={{ marginBottom: '12px' }}>
                      <label htmlFor="desde" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Desde *</label>
                      <input
                        type="number"
                        id="desde"
                        className="form-control"
                        value={desde}
                        onChange={handleDesdeChange}
                        placeholder="Número desde"
                        required
                        style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: '12px' }}>
                      <label htmlFor="hasta" style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Hasta *</label>
                      <input
                        type="number"
                        id="hasta"
                        className="form-control"
                        value={hasta}
                        onChange={handleHastaChange}
                        placeholder="Número hasta"
                        required
                        style={{ fontSize: '13px', height: '32px', padding: '4px 8px' }}
                      />
                    </div>
                  </>
                );
              })()}

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <button 
                  className="btn w-100"
                  onClick={handleEnviar}
                  disabled={cargando}
                  style={{ 
                    height: '38px', 
                    fontSize: '14px', 
                    fontWeight: 600,
                    backgroundColor: theme.colors.primary,
                    borderColor: theme.colors.primary,
                    color: theme.colors.textLight,
                    borderRadius: theme.styles.borderRadius,
                    boxShadow: theme.styles.boxShadowLight,
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!cargando) {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow = theme.styles.boxShadowLight;
                  }}
                >
                  {cargando ? 'Enviando...' : 'Enviar'}
                </button>
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <div className="row" style={{ gap: '8px', margin: 0 }}>
                  <div className="col-6" style={{ padding: 0 }}>
                    <button 
                      className="btn w-100"
                      onClick={handleResumir}
                      disabled={cargando || aplicadas.length === 0}
                      style={{ 
                        height: '36px', 
                        fontSize: '13px', 
                        fontWeight: 600,
                        backgroundColor: theme.colors.info,
                        borderColor: theme.colors.info,
                        color: theme.colors.textLight,
                        borderRadius: theme.styles.borderRadius,
                        boxShadow: theme.styles.boxShadowLight,
                        transition: 'all 0.3s ease',
                        width: '100%'
                      }}
                      onMouseEnter={(e) => {
                        if (!cargando && aplicadas.length > 0) {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = '';
                        e.currentTarget.style.boxShadow = theme.styles.boxShadowLight;
                      }}
                    >
                      Resumir
                    </button>
                  </div>
                  <div className="col-6" style={{ padding: 0 }}>
                    <button 
                      className="btn w-100 btn-grabar"
                      onClick={handleGrabar}
                      disabled={grabarCargando || !isGrabarEnabled()}
                      style={{ 
                        height: '36px', 
                        fontSize: '13px', 
                        fontWeight: 600,
                        borderRadius: theme.styles.borderRadius,
                        width: '100%'
                      }}
                    >
                      {grabarCargando ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" style={{ width: '0.8rem', height: '0.8rem', borderColor: `${theme.colors.textLight} transparent ${theme.colors.textLight} ${theme.colors.textLight}` }}></span>
                          Grabando...
                        </>
                      ) : (
                        'Grabar'
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '0' }}>
                <div style={{ 
                  display: 'flex', 
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  <button 
                    className="btn"
                    onClick={handleTildarTodos}
                    disabled={cargando || aplicadas.length === 0}
                    style={{ 
                      flex: '1 1 0',
                      minWidth: '100px',
                      height: '36px', 
                      fontSize: '13px', 
                      fontWeight: 600,
                      backgroundColor: theme.colors.secondary,
                      borderColor: theme.colors.secondary,
                      color: theme.colors.textLight,
                      borderRadius: theme.styles.borderRadius,
                      boxShadow: theme.styles.boxShadowLight,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      if (!cargando && aplicadas.length > 0) {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                        e.currentTarget.style.backgroundColor = theme.colors.secondaryBright;
                        e.currentTarget.style.borderColor = theme.colors.secondaryBright;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = '';
                      e.currentTarget.style.boxShadow = theme.styles.boxShadowLight;
                      e.currentTarget.style.backgroundColor = theme.colors.secondary;
                      e.currentTarget.style.borderColor = theme.colors.secondary;
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>✓</span>
                    Tildar Todos
                  </button>
                  <button 
                    className="btn"
                    onClick={handleDestildarTodos}
                    disabled={cargando || aplicadas.length === 0}
                    style={{ 
                      flex: '1 1 0',
                      minWidth: '100px',
                      height: '36px', 
                      fontSize: '13px', 
                      fontWeight: 600,
                      backgroundColor: theme.colors.info,
                      borderColor: theme.colors.info,
                      color: theme.colors.textLight,
                      borderRadius: theme.styles.borderRadius,
                      boxShadow: theme.styles.boxShadowLight,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      if (!cargando && aplicadas.length > 0) {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                        e.currentTarget.style.backgroundColor = theme.colors.primary;
                        e.currentTarget.style.borderColor = theme.colors.primary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = '';
                      e.currentTarget.style.boxShadow = theme.styles.boxShadowLight;
                      e.currentTarget.style.backgroundColor = theme.colors.info;
                      e.currentTarget.style.borderColor = theme.colors.info;
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>✗</span>
                    Destildar Todos
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card h-100" style={{ marginBottom: '16px', borderRadius: theme.styles.borderRadius, boxShadow: theme.styles.boxShadowLight }}>
            <div className="card-header text-white text-center" style={{ backgroundColor: theme.colors.primaryDark, padding: '10px 12px', fontSize: '14px', fontWeight: 600, borderRadius: `${theme.styles.borderRadius} ${theme.styles.borderRadius} 0 0` }}>
              <h5 className="m-0" style={{ fontSize: '14px', fontWeight: 600 }}>Control</h5>
            </div>
            <div className="card-body control-table-container" style={{ padding: '12px' }}>
              {aplicadas.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm control-table">
                    <thead className="sticky-header">
                      <tr>
                        {(() => {
                          const productoSeleccionado = productos.find(p => p.id.toString() === producto);
                          const esTipo3 = productoSeleccionado?.tipo === 3;
                          
                          if (esTipo3) {
                            return (
                              <>
                                <th>Nº Silo</th>
                                <th>Carga</th>
                                <th>Aplicado</th>
                              </>
                            );
                          } else {
                            return (
                              <>
                                <th>NºFardo</th>
                                <th>Aplicado</th>
                                <th>Stock</th>
                                <th>Orden</th>
                                <th>Alta/Baja</th>
                              </>
                            );
                          }
                        })()}
                      </tr>
                    </thead>
                    <tbody>
                      {aplicadas.map((item) => {
                        const productoSeleccionado = productos.find(p => p.id.toString() === producto);
                        const esTipo3 = productoSeleccionado?.tipo === 3;
                        
                        if (esTipo3) {
                          // Renderizado para Producto Tipo 3
                          return (
                            <tr key={item.id}>
                              <td>
                                {item.id === 0 ? 'GRANEL' : item.descripcion}
                              </td>
                              <td className="text-center fw-bold">
                                {item.cantidad}
                              </td>
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={item.aplicado || false}
                                  onChange={(e) => handleAplicacionChange(item.id, e.target.checked)}
                                />
                              </td>
                            </tr>
                          );
                        } else {
                          // Renderizado para otros tipos de producto
                          return (
                            <tr key={item.id}>
                              <td>{item.id}</td>
                              <td>
                                {item.aplicado ? (
                                  <span style={{ color: theme.colors.secondary }}>✅</span>
                                ) : (
                                  <span style={{ color: theme.colors.error }}>❌</span>
                                )}
                              </td>
                              <td>
                                {(item.stock || 0) > 0 ? (
                                  <span style={{ color: theme.colors.secondary }}>✅</span>
                                ) : (
                                  <span style={{ color: theme.colors.error }}>❌</span>
                                )}
                              </td>
                              <td>
                                {item.orden === 'NO' ? (
                                  <span style={{ color: theme.colors.secondary }}>{item.orden}</span>
                                ) : (
                                  <span style={{ color: theme.colors.error }}>{item.orden}</span>
                                )}
                              </td>
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={item.orden === 'NO' ? (item.aplicado || false) : false}
                                  disabled={
                                    item.orden !== 'NO' || 
                                    (tipoOrden === 'C' && !item.aplicado) ||
                                    (tipoOrden === 'V' && (item.stock || 0) <= 0)
                                  }
                                  onChange={(e) => handleAplicacionChange(item.id, e.target.checked)}
                                  title={
                                    item.orden !== 'NO' 
                                      ? 'No disponible - Fardo en orden'
                                      : tipoOrden === 'C' && !item.aplicado
                                        ? 'No disponible - Fardo no está en Aplicación (requerido para Compra)'
                                        : tipoOrden === 'V' && (item.stock || 0) <= 0
                                          ? 'No disponible - Fardo no está en Stock (requerido para Venta)'
                                          : 'Seleccionar fardo'
                                  }
                                />
                              </td>
                            </tr>
                          );
                        }
                      })}
                    </tbody>

                  </table>
                </div>
              ) : (
                <p className="text-muted text-center">No hay datos de control disponibles</p>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card h-100" style={{ marginBottom: '16px', borderRadius: theme.styles.borderRadius, boxShadow: theme.styles.boxShadowLight }}>
            <div className="card-header text-white text-center" style={{ backgroundColor: theme.colors.primaryDark, padding: '10px 12px', fontSize: '14px', fontWeight: 600, borderRadius: `${theme.styles.borderRadius} ${theme.styles.borderRadius} 0 0` }}>
              <h5 className="m-0" style={{ fontSize: '14px', fontWeight: 600 }}>Resumen</h5>
            </div>
            <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto', padding: '12px' }}>
              {(() => {
                const productoSeleccionado = productos.find(p => p.id.toString() === producto);
                const esTipo3 = productoSeleccionado?.tipo === 3;
                
                                 if (esTipo3) {
                   // Resumen específico para Producto Tipo 3
                   if (resumen && resumen.totalSilos > 0) {
                     return (
                       <div className="table-responsive">
                         <table className="table table-sm">
                           <thead>
                             <tr>
                               <th>Concepto</th>
                               <th>Cantidad</th>
                             </tr>
                           </thead>
                           <tbody>
                             <tr>
                               <td>Total Silos</td>
                               <td className="text-center fw-bold">{resumen.totalSilos}</td>
                             </tr>
                             <tr>
                               <td>Total Cargas</td>
                              <td className="text-center fw-bold" style={{ color: theme.colors.secondary }}>
                                {resumen.totalCargas}
                              </td>
                            </tr>
                            <tr>
                              <td>Aplicados</td>
                              <td className="text-center fw-bold" style={{ color: theme.colors.secondary }}>
                                {resumen.aplicados}
                              </td>
                            </tr>
                            <tr>
                              <td>No Aplicados</td>
                              <td className="text-center fw-bold" style={{ color: theme.colors.error }}>
                                {resumen.noAplicados}
                              </td>
                             </tr>
                           </tbody>
                         </table>
                       </div>
                     );
                   } else if (aplicadas.length > 0) {
                     // Mostrar datos del control si no hay resumen aún
                     const totalSilos = aplicadas.length;
                     const totalCargas = aplicadas.reduce((total, item) => total + (item.cantidad || 0), 0);
                     const aplicados = aplicadas.filter(item => item.aplicado).length;
                     
                     return (
                       <div className="table-responsive">
                         <table className="table table-sm">
                           <thead>
                             <tr>
                               <th>Concepto</th>
                               <th>Cantidad</th>
                             </tr>
                           </thead>
                           <tbody>
                             <tr>
                               <td>Total Silos</td>
                               <td className="text-center fw-bold">{totalSilos}</td>
                             </tr>
                             <tr>
                               <td>Total Cargas</td>
                              <td className="text-center fw-bold" style={{ color: theme.colors.secondary }}>
                                {totalCargas}
                              </td>
                            </tr>
                            <tr>
                              <td>Aplicados</td>
                              <td className="text-center fw-bold" style={{ color: theme.colors.secondary }}>
                                {aplicados}
                              </td>
                            </tr>
                            <tr>
                              <td>No Aplicados</td>
                              <td className="text-center fw-bold" style={{ color: theme.colors.error }}>
                                {totalSilos - aplicados}
                              </td>
                             </tr>
                           </tbody>
                         </table>
                       </div>
                     );
                   } else {
                     return <p className="text-muted text-center">No hay datos de resumen disponibles</p>;
                   }
                } else {
                  // Resumen para otros tipos de producto
                  if (resumen) {
                    return (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Concepto</th>
                              <th>Cantidad</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>Total Fardos</td>
                              <td className="text-center fw-bold">{resumen.totalFardos}</td>
                            </tr>
                            <tr>
                              <td>Aplicados</td>
                              <td className="text-center fw-bold" style={{ color: theme.colors.secondary }}>
                                {resumen.aplicados}
                              </td>
                            </tr>
                            <tr>
                              <td>No Aplicados</td>
                              <td className="text-center fw-bold" style={{ color: theme.colors.error }}>
                                {resumen.noAplicados}
                              </td>
                            </tr>
                            <tr>
                              <td>Con Stock</td>
                              <td className="text-center fw-bold" style={{ color: theme.colors.secondary }}>
                                {resumen.conStock}
                              </td>
                            </tr>
                            <tr>
                              <td>Sin Stock</td>
                              <td className="text-center fw-bold" style={{ color: theme.colors.error }}>
                                {resumen.sinStock}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  } else {
                    return <p className="text-muted text-center">No hay datos de resumen disponibles</p>;
                  }
                }
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AltaOrdenes; 